/**
 * Analytics Utility — Centralized event tracking for ComicMenia
 * 
 * Uses PostHog (already initialized in index.html) for:
 * - Page views (auto-captured)
 * - Custom events (comic creation, AI generation, purchases, etc.)
 * - User identification (login/signup)
 * - Funnel tracking (signup → create → generate → publish → monetize)
 * 
 * Also fires to Microsoft Clarity if available.
 */

// ── PostHog Event Tracking ──

/**
 * Track a custom event with properties.
 * @param {string} event - Event name (e.g., 'comic_created', 'ai_panel_generated')
 * @param {object} properties - Event properties
 */
export const trackEvent = (event, properties = {}) => {
  try {
    if (window.posthog && typeof window.posthog.capture === 'function') {
      window.posthog.capture(event, {
        ...properties,
        timestamp: new Date().toISOString(),
      });
    }
  } catch (e) {
    // Silent fail — analytics should never break the app
  }
};

/**
 * Identify a user after login/signup.
 * Links all anonymous activity to their user profile.
 * @param {string} userId - Unique user ID
 * @param {object} traits - User traits (username, email, plan, etc.)
 */
export const identifyUser = (userId, traits = {}) => {
  try {
    if (window.posthog && typeof window.posthog.identify === 'function') {
      window.posthog.identify(userId, {
        ...traits,
        identified_at: new Date().toISOString(),
      });
    }
    // Also tag in Clarity if available
    if (window.clarity && typeof window.clarity === 'function') {
      window.clarity('identify', userId);
      if (traits.username) window.clarity('set', 'username', traits.username);
      if (traits.email) window.clarity('set', 'email', traits.email);
    }
  } catch (e) {
    // Silent fail
  }
};

/**
 * Reset tracking (on logout).
 */
export const resetTracking = () => {
  try {
    if (window.posthog && typeof window.posthog.reset === 'function') {
      window.posthog.reset();
    }
  } catch (e) {
    // Silent fail
  }
};

// ── Pre-defined Event Helpers ──

/** User signed up */
export const trackSignup = (provider = 'email') => {
  trackEvent('user_signed_up', { provider });
};

/** User logged in */
export const trackLogin = (provider = 'email') => {
  trackEvent('user_logged_in', { provider });
};

/** Comic project created */
export const trackComicCreated = (properties = {}) => {
  trackEvent('comic_created', {
    panel_count: properties.panelCount,
    art_style: properties.artStyle,
    orientation: properties.orientation,
    ...properties,
  });
};

/** AI panel generated */
export const trackAIGeneration = (properties = {}) => {
  trackEvent('ai_panel_generated', {
    model_engine: properties.modelEngine,
    success: properties.success,
    generation_time: properties.generationTime,
    panel_count: properties.panelCount,
    face_lock: properties.faceLock,
    ...properties,
  });
};

/** Credits purchased */
export const trackCreditPurchase = (properties = {}) => {
  trackEvent('credits_purchased', {
    credits: properties.credits,
    amount: properties.amount,
    currency: properties.currency || 'INR',
    ...properties,
  });
};

/** Comic published */
export const trackComicPublished = (properties = {}) => {
  trackEvent('comic_published', {
    comic_id: properties.comicId,
    page_count: properties.pageCount,
    ...properties,
  });
};

/** Tip sent to creator */
export const trackTipSent = (properties = {}) => {
  trackEvent('tip_sent', {
    amount: properties.amount,
    currency: properties.currency || 'INR',
    ...properties,
  });
};

/** Editor opened */
export const trackEditorOpened = (properties = {}) => {
  trackEvent('editor_opened', {
    project_id: properties.projectId,
    ...properties,
  });
};

/** Character created in Character Studio */
export const trackCharacterCreated = (properties = {}) => {
  trackEvent('character_created', {
    has_image: properties.hasImage,
    auto_described: properties.autoDescribed,
    ...properties,
  });
};

/** Page added to comic */
export const trackPageAdded = (properties = {}) => {
  trackEvent('page_added', {
    page_number: properties.pageNumber,
    panel_count: properties.panelCount,
    model_engine: properties.modelEngine,
    ...properties,
  });
};

/** Sketch-to-AI generation */
export const trackSketchToAI = (properties = {}) => {
  trackEvent('sketch_to_ai_generated', {
    model_engine: properties.modelEngine,
    success: properties.success,
    ...properties,
  });
};
